import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TripComponent, ComponentType, flightSchema, componentTypeSchema } from "@shared/schema";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { format, differenceInCalendarDays, addDays } from "date-fns";
import { 
  Plane as PlaneIcon, 
  Building as BuildingIcon, 
  Smile as SmileIcon, 
  Truck as TruckIcon, 
  Utensils as UtensilsIcon, 
  File as FileIcon,
  X as XIcon,
  Plus as PlusIcon,
  ArrowRight as ArrowRightIcon,
  Users as UsersIcon,
  Calendar as CalendarIcon,
  ArrowLeftRight as ArrowLeftRightIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Loader as LoaderIcon,
  PlaneTakeoff as PlaneTakeoffIcon,
  PlaneLanding as PlaneLandingIcon,
  MapPin as MapPinIcon,
  Ship,
  Shield,
  Package,
  Star,
  Wifi,
  Car,
  Bed,
  Coffee,
  Utensils,
  Clock,
  DollarSign
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import SimpleAirportInput from "./SimpleAirportInput";
import { ensureComponentType } from "@/lib/componentTypes";

// Define schemas for different component types
const hotelSchema = z.object({
  hotelName: z.string().optional(),
  hotelChain: z.string().optional(),
  roomType: z.string().optional(),
  bedType: z.string().optional(),
  numberOfRooms: z.number().default(1),
  numberOfGuests: z.number().default(1),
  checkInDate: z.string().optional(),
  checkOutDate: z.string().optional(),
  checkInTime: z.string().optional(),
  checkOutTime: z.string().optional(),
  confirmationNumber: z.string().optional(),
  specialRequests: z.string().optional(),
  amenities: z.array(z.string()).default([]),
  rating: z.number().optional(),
  pricePerNight: z.number().optional(),
  totalPrice: z.number().optional(),
  cancellationPolicy: z.string().optional(),
});

const cruiseSchema = z.object({
  cruiseLine: z.string().optional(),
  shipName: z.string().optional(),
  cabinType: z.string().optional(),
  cabinNumber: z.string().optional(),
  deckLevel: z.string().optional(),
  numberOfGuests: z.number().default(1),
  embarkationPort: z.string().optional(),
  disembarkationPort: z.string().optional(),
  embarkationDate: z.string().optional(),
  disembarkationDate: z.string().optional(),
  embarkationTime: z.string().optional(),
  disembarkationTime: z.string().optional(),
  confirmationNumber: z.string().optional(),
  diningPackage: z.string().optional(),
  beveragePackage: z.string().optional(),
  wifiPackage: z.string().optional(),
  excursions: z.array(z.string()).default([]),
  specialRequests: z.string().optional(),
  totalPrice: z.number().optional(),
});

const travelInsuranceSchema = z.object({
  provider: z.string().optional(),
  policyNumber: z.string().optional(),
  policyType: z.string().optional(),
  coverageAmount: z.number().optional(),
  premium: z.number().optional(),
  effectiveDate: z.string().optional(),
  expirationDate: z.string().optional(),
  beneficiaries: z.array(z.string()).default([]),
  coverageDetails: z.array(z.string()).default([]),
  emergencyContactNumber: z.string().optional(),
  claimsContactNumber: z.string().optional(),
  preExistingConditions: z.string().optional(),
  specialNotes: z.string().optional(),
});

const tourPackageSchema = z.object({
  tourOperator: z.string().optional(),
  packageName: z.string().optional(),
  tourType: z.string().optional(),
  groupSize: z.number().optional(),
  tourGuide: z.string().optional(),
  guideLanguage: z.string().optional(),
  meetingPoint: z.string().optional(),
  meetingTime: z.string().optional(),
  duration: z.string().optional(),
  inclusions: z.array(z.string()).default([]),
  exclusions: z.array(z.string()).default([]),
  difficultyLevel: z.string().optional(),
  ageRestrictions: z.string().optional(),
  confirmationNumber: z.string().optional(),
  totalPrice: z.number().optional(),
  cancellationPolicy: z.string().optional(),
  specialRequirements: z.string().optional(),
});

const packageSchema = z.object({
  supplier: z.string().optional(),
  packageName: z.string().optional(),
  packageType: z.string().optional(), // all-inclusive, traditional, etc.
  resortName: z.string().optional(),
  roomType: z.string().optional(),
  boardBasis: z.string().optional(), // all-inclusive, half-board, etc.
  numberOfGuests: z.number().default(1),
  checkInDate: z.string().optional(),
  checkOutDate: z.string().optional(),
  confirmationNumber: z.string().optional(),
  totalPrice: z.number().optional(),
  // Flight details (always included in packages)
  includesAir: z.boolean().default(true),
  flightDetails: flightSchema.optional(),
  specialRequests: z.string().optional(),
});

const activitySchema = z.object({
  activityType: z.string().optional(),
  provider: z.string().optional(),
  duration: z.string().optional(),
  numberOfParticipants: z.number().default(1),
  meetingPoint: z.string().optional(),
  meetingTime: z.string().optional(),
  endTime: z.string().optional(),
  difficultyLevel: z.string().optional(),
  ageRestrictions: z.string().optional(),
  equipmentProvided: z.array(z.string()).default([]),
  equipmentRequired: z.array(z.string()).default([]),
  confirmationNumber: z.string().optional(),
  totalPrice: z.number().optional(),
  cancellationPolicy: z.string().optional(),
  specialRequirements: z.string().optional(),
  weatherDependent: z.boolean().default(false),
});

const transportSchema = z.object({
  transportType: z.string().optional(),
  provider: z.string().optional(),
  vehicleType: z.string().optional(),
  pickupLocation: z.string().optional(),
  dropoffLocation: z.string().optional(),
  pickupTime: z.string().optional(),
  dropoffTime: z.string().optional(),
  numberOfPassengers: z.number().default(1),
  driverName: z.string().optional(),
  driverContact: z.string().optional(),
  confirmationNumber: z.string().optional(),
  totalPrice: z.number().optional(),
  specialRequests: z.string().optional(),
  vehicleFeatures: z.array(z.string()).default([]),
});

const foodSchema = z.object({
  restaurantName: z.string().optional(),
  cuisineType: z.string().optional(),
  mealType: z.string().optional(),
  reservationTime: z.string().optional(),
  numberOfGuests: z.number().default(1),
  confirmationNumber: z.string().optional(),
  specialRequests: z.string().optional(),
  dietaryRestrictions: z.array(z.string()).default([]),
  priceRange: z.string().optional(),
  dressCode: z.string().optional(),
  contactNumber: z.string().optional(),
});

const baseComponentShape = {
  tripId: z.number(),
  type: componentTypeSchema,
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  location: z.string().optional(),
  startDate: z.string(),
  endDate: z.string().optional(),
  dayIndex: z.number(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  details: z.unknown().optional(),
};

const createSchemaForType = <T extends ComponentType, D extends z.ZodTypeAny>(
  type: T,
  options: { requireEndDate?: boolean; detailsSchema?: D } = {}
) =>
  z.object({
    ...baseComponentShape,
    type: z.literal(type),
    endDate: options.requireEndDate ? z.string() : baseComponentShape.endDate,
    details: options.detailsSchema
      ? options.detailsSchema.optional()
      : baseComponentShape.details,
  });

const componentSchemas = {
  flight: createSchemaForType("flight", { detailsSchema: flightSchema }),
  hotel: createSchemaForType("hotel", { requireEndDate: true, detailsSchema: hotelSchema }),
  cruise: createSchemaForType("cruise", { requireEndDate: true, detailsSchema: cruiseSchema }),
  "travel-insurance": createSchemaForType("travel-insurance", {
    requireEndDate: true,
    detailsSchema: travelInsuranceSchema,
  }),
  tour: createSchemaForType("tour", { detailsSchema: tourPackageSchema }),
  package: createSchemaForType("package", { requireEndDate: true, detailsSchema: packageSchema }),
  activity: createSchemaForType("activity", { detailsSchema: activitySchema }),
  transport: createSchemaForType("transport", { detailsSchema: transportSchema }),
  food: createSchemaForType("food", { detailsSchema: foodSchema }),
  other: createSchemaForType("other"),
} satisfies Record<ComponentType, z.ZodTypeAny>;

type ComponentSchemaMap = typeof componentSchemas;
type ComponentFormValues = z.infer<ComponentSchemaMap[keyof ComponentSchemaMap]>;

const endDateRequiredTypes: ComponentType[] = [
  "hotel",
  "cruise",
  "travel-insurance",
  "package",
];

const typeRequiresEndDate = (type: ComponentType) =>
  endDateRequiredTypes.includes(type);

interface AddComponentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: number;
  type?: ComponentType;
  dayIndex?: number;
  startDate: Date | null;
  endDate: Date | null;
  onSuccess: () => void;
  // Optional existing component for editing
  component?: TripComponent | null;
}

export default function AddComponentModal({
  open,
  onOpenChange,
  tripId,
  type,
  dayIndex,
  startDate,
  endDate,
  onSuccess,
  component
}: AddComponentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [componentType, setComponentType] = useState<ComponentType>(() =>
    ensureComponentType(type)
  );
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [flightSearchTab, setFlightSearchTab] = useState<'search' | 'results'>('search');
  const [flightResults, setFlightResults] = useState<any[]>([]);
  const [selectedFlight, setSelectedFlight] = useState<any>(null);
  
  const { toast } = useToast();
  
  // Function to generate dynamic title for flight
  const generateFlightTitle = (details: any) => {
    if (!details || !details.legs || !details.legs[0]) return "New Flight";
    
    const leg = details.legs[0];
    const from = leg.departureAirport || "Origin";
    const to = leg.arrivalAirport || "Destination";
    
    let flightInfo = "";
    if (leg.segments && leg.segments[0] && leg.segments[0].airline) {
      flightInfo = ` - ${leg.segments[0].airline}`;
      if (leg.segments[0].flightNumber) {
        flightInfo += ` ${leg.segments[0].flightNumber}`;
      }
    }
    
    return `${from} to ${to}${flightInfo}`;
  };

  const hasTripDates = startDate != null && endDate != null;
  const effectiveStart = startDate ?? new Date();
  const effectiveEnd = endDate ?? new Date();
  const dateRange: [Date, Date] | null = hasTripDates ? [effectiveStart, effectiveEnd] : null;
  
  const schema = useMemo(() => componentSchemas[componentType], [componentType]);
  const defaultStartDate = hasTripDates ? format(effectiveStart, "yyyy-MM-dd") : "";
  const defaultEndDate = hasTripDates ? format(effectiveEnd, "yyyy-MM-dd") : "";

  const form = useForm<ComponentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      tripId,
      type: componentType,
      title: componentType === "flight" ? "New Flight" : "",
      description: undefined,
      location: undefined,
      startDate: defaultStartDate,
      endDate: typeRequiresEndDate(componentType) ? defaultEndDate : undefined,
      dayIndex: dayIndex ?? 0,
      startTime: undefined,
      endTime: undefined,
      details:
        componentType === "flight"
          ? {
              tripType: "roundTrip",
              passengers: 1,
              cabinClass: "economy",
              legs: [
                {
                  departureAirport: "",
                  arrivalAirport: "",
                  departureDate: defaultStartDate,
                  segments: [{}],
                },
              ],
            }
          : undefined,
    },
  });

  useEffect(() => {
    const nextType = ensureComponentType(type);
    if (nextType !== componentType) {
      setComponentType(nextType);
      form.setValue("type", nextType);
    }
  }, [type, componentType, form]);
  
  const calculateDayIndex = (date: string): number => {
    if (!dateRange) {
      // No trip dates available — we can't compute a meaningful dayIndex.
      // Preserve whatever dayIndex is already on the form (or the caller-
      // supplied prop as a last resort) rather than silently overwriting to 0,
      // which would corrupt the saved component's day ordering for trips
      // without a defined date range.
      console.warn(
        '[AddComponentModal] calculateDayIndex called without trip dates — preserving existing dayIndex (no trip date range to anchor to).',
      );
      const current = form.getValues('dayIndex');
      return typeof current === 'number' ? current : (dayIndex ?? 0);
    }
    const selectedDate = new Date(date);
    return differenceInCalendarDays(selectedDate, dateRange[0]);
  };

  const calculateDate = (dayIdx: number): string => {
    if (!dateRange) {
      console.warn(
        '[AddComponentModal] calculateDate called without trip dates — returning today as fallback.',
      );
      return format(new Date(), "yyyy-MM-dd");
    }
    const date = addDays(dateRange[0], dayIdx);
    return format(date, "yyyy-MM-dd");
  };
  
  // Dummy function to search for flights - will be replaced with actual API call later
  const searchFlights = (searchParams: any) => {
    setIsSearching(true);
    
    // Simulate API delay
    setTimeout(() => {
      // Generate dummy flight results
      const dummyFlights = [
        {
          id: "f1",
          airline: "WorldVia Airways",
          price: 450,
          duration: "2h 15m",
          departureTime: "08:30",
          arrivalTime: "10:45",
          departureAirport: searchParams.from || "JFK",
          departureCity: "New York",
          departureAirportCode: "JFK",
          arrivalAirport: searchParams.to || "BOS",
          arrivalCity: "Boston",
          arrivalAirportCode: "BOS",
          stops: 0,
          flightNumber: "WV123",
          cabinClass: searchParams.cabinClass || "economy",
          date: searchParams.departureDate || format(new Date(), "yyyy-MM-dd")
        },
        {
          id: "f2",
          airline: "SkyJet Air",
          price: 380,
          duration: "2h 30m",
          departureTime: "10:15",
          arrivalTime: "12:45",
          departureAirport: searchParams.from || "JFK",
          departureCity: "New York",
          departureAirportCode: "JFK",
          arrivalAirport: searchParams.to || "BOS",
          arrivalCity: "Boston",
          arrivalAirportCode: "BOS",
          stops: 0,
          flightNumber: "SJ456",
          cabinClass: searchParams.cabinClass || "economy",
          date: searchParams.departureDate || format(new Date(), "yyyy-MM-dd")
        },
        {
          id: "f3",
          airline: "Global Connect",
          price: 320,
          duration: "2h 45m",
          departureTime: "14:00",
          arrivalTime: "16:45",
          departureAirport: searchParams.from || "JFK",
          departureCity: "New York",
          departureAirportCode: "JFK",
          arrivalAirport: searchParams.to || "BOS",
          arrivalCity: "Boston",
          arrivalAirportCode: "BOS",
          stops: 1,
          stopAirports: ["PHL"],
          stopDurations: ["45m"],
          flightNumber: "GC789",
          cabinClass: searchParams.cabinClass || "economy",
          date: searchParams.departureDate || format(new Date(), "yyyy-MM-dd")
        }
      ];
      
      setFlightResults(dummyFlights);
      setFlightSearchTab('results');
      setIsSearching(false);
    }, 1500);
  };
  
  // Function to select a flight and update form
  const handleSelectFlight = (flight: any) => {
    setSelectedFlight(flight);
    
    // Update form with flight details
    const details = form.getValues("details") || {};
    const updatedDetails = {
      ...details,
      legs: [{
        departureAirport: flight.departureAirport,
        departureCity: flight.departureCity,
        departureAirportCode: flight.departureAirportCode,
        arrivalAirport: flight.arrivalAirport,
        arrivalCity: flight.arrivalCity,
        arrivalAirportCode: flight.arrivalAirportCode,
        departureDate: flight.date,
        segments: [{
          airline: flight.airline,
          flightNumber: flight.flightNumber,
          departureTime: flight.departureTime,
          arrivalTime: flight.arrivalTime
        }]
      }]
    };
    
    form.setValue("details", updatedDetails);
    
    // Update form values
    form.setValue("startDate", flight.date);
    form.setValue("startTime", flight.departureTime);
    form.setValue("endTime", flight.arrivalTime);
    
    // Calculate day index
    const newDayIndex = calculateDayIndex(flight.date);
    form.setValue("dayIndex", newDayIndex);
    
    // Set location
    form.setValue("location", `${flight.departureAirportCode} to ${flight.arrivalAirportCode}`);
    
    toast({
      title: "Flight Selected",
      description: `Selected ${flight.airline} flight ${flight.flightNumber}`,
    });
  };

  // Initialize form with component data when editing
  useEffect(() => {
    if (!component) {
      return;
    }

    const nextType = ensureComponentType(component.type);
    setComponentType(nextType);
    form.setValue("type", nextType);
    form.setValue("title", component.title || "");
    form.setValue("description", component.description || "");
    form.setValue("location", component.location || "");
    form.setValue("dayIndex", component.dayIndex ?? 0);
    form.setValue("tripId", component.tripId);

    const startDateStr = component.startDate
      ? format(new Date(component.startDate), "yyyy-MM-dd")
      : defaultStartDate;
    form.setValue("startDate", startDateStr);

    if (component.startTime) {
      form.setValue("startTime", component.startTime);
    } else {
      form.setValue("startTime", undefined);
    }

    if (component.endTime) {
      form.setValue("endTime", component.endTime);
    } else {
      form.setValue("endTime", undefined);
    }

    if (component.endDate) {
      form.setValue(
        "endDate",
        format(new Date(component.endDate), "yyyy-MM-dd")
      );
    } else if (typeRequiresEndDate(nextType)) {
      form.setValue("endDate", defaultEndDate);
    } else {
      form.setValue("endDate", undefined);
    }

    if (component.details) {
      form.setValue("details", component.details as ComponentFormValues["details"]);
    } else if (nextType === "flight") {
      form.setValue("details", form.getValues("details"));
    } else {
      form.setValue("details", undefined);
    }
  }, [component, form, defaultEndDate, defaultStartDate]);
  
  // Update form values when component type changes (only if not editing)
  useEffect(() => {
    if (!component) {
      form.setValue("type", componentType);

      if (typeRequiresEndDate(componentType)) {
        const currentEndDate = form.getValues("endDate");
        form.setValue("endDate", currentEndDate || defaultEndDate);
      } else {
        form.setValue("endDate", undefined);
      }

      // Reset details when changing component type with appropriate defaults
      if (componentType === "flight") {
        // Initialize flight details
        form.setValue("details", {
          tripType: "roundTrip",
          passengers: 1,
          cabinClass: "economy",
          legs: [{
            departureAirport: "",
            arrivalAirport: "",
            departureDate: form.getValues("startDate") || defaultStartDate,
            segments: [{}]
          }]
        });
        // Set a default title for flights
        if (!form.getValues("title")) {
          form.setValue("title", "New Flight");
        }
      } else {
        // Reset to empty for other component types
        form.setValue("details", undefined);
        // Clear the title if switching from flight
        if (form.getValues("title") === "New Flight") {
          form.setValue("title", "");
        }
      }
    }
  }, [componentType, component, defaultEndDate, defaultStartDate, form]);
  
  const handleSubmit = async (data: ComponentFormValues) => {
    const resolvedTitle =
      componentType === "flight"
        ? generateFlightTitle(data.details)
        : data.title || "Untitled";

    setIsSubmitting(true);

    try {
      const convertDate = (value: string) =>
        new Date(`${value}T00:00:00`).toISOString();
      const convertOptionalDate = (value?: string) =>
        value ? convertDate(value) : undefined;

      const startDateIso = convertDate(data.startDate);
      const requestedEndDate = data.endDate ?? undefined;
      const endDateIso = convertOptionalDate(requestedEndDate);

      const formattedData: {
        tripId: number;
        type: ComponentType;
        title: string;
        description: string;
        startDate: string;
        endDate: string | null | undefined;
        location: string;
        dayIndex: number;
        startTime?: string;
        endTime?: string;
        details?: ComponentFormValues["details"];
      } = {
        tripId,
        type: componentType,
        title: resolvedTitle || "Untitled",
        description: data.description ?? "",
        startDate: startDateIso,
        endDate: null,
        location: data.location ?? "",
        dayIndex: data.dayIndex ?? dayIndex ?? 0,
        startTime: data.startTime || undefined,
        endTime: data.endTime || undefined,
        details: data.details ?? undefined,
      };

      if (typeRequiresEndDate(componentType)) {
        formattedData.endDate =
          endDateIso ?? convertOptionalDate(defaultEndDate) ?? formattedData.startDate;
      } else if (endDateIso) {
        formattedData.endDate = endDateIso;
      }

      let result;
      
      if (component?.id) {
        // Update existing component
        result = await apiRequest("PATCH", `/api/components/${component.id}`, formattedData);
        toast({
          title: "Success",
          description: "Component updated successfully",
        });
      } else {
        // Create new component
        result = await apiRequest("POST", "/api/components", formattedData);
        toast({
          title: "Success",
          description: "Component added successfully",
        });
      }
      
      onSuccess();
    } catch (error) {
      console.error("Error saving component:", error);
      // If it's a response with details, log them
      if (error && typeof error === 'object' && 'data' in error) {
        console.error("API error details:", error.data);
      }
      
      // Display error toast
      toast({
        title: "Error",
        description: "Failed to save component. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getComponentIcon = (type: ComponentType) => {
    switch (type) {
      case "flight": return <PlaneIcon className="h-6 w-6" />;
      case "hotel": return <BuildingIcon className="h-6 w-6" />;
      case "cruise": return <Ship className="h-6 w-6" />;
      case "travel-insurance": return <Shield className="h-6 w-6" />;
      case "tour": return <Package className="h-6 w-6" />;
      case "package": return <Package className="h-6 w-6" />;
      case "activity": return <SmileIcon className="h-6 w-6" />;
      case "transport": return <TruckIcon className="h-6 w-6" />;
      case "food": return <UtensilsIcon className="h-6 w-6" />;
      default: return <FileIcon className="h-6 w-6" />;
    }
  };

  const getComponentColor = (type: ComponentType) => {
    switch (type) {
      case "flight": return "bg-blue-100 text-primary";
      case "hotel": return "bg-orange-100 text-secondary";
      case "cruise": return "bg-blue-100 text-blue-600";
      case "travel-insurance": return "bg-green-100 text-green-600";
      case "tour": return "bg-emerald-100 text-emerald-600";
      case "package": return "bg-purple-100 text-purple-600";
      case "activity": return "bg-green-100 text-accent";
      case "transport": return "bg-purple-100 text-purple-600";
      case "food": return "bg-red-100 text-red-600";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getModalTitle = () => {
    const action = component ? "Edit" : "Add New";
    switch (componentType) {
      case "flight": return `${action} Flight`;
      case "hotel": return `${action} Hotel`;
      case "cruise": return `${action} Cruise`;
      case "travel-insurance": return `${action} Travel Insurance`;
      case "tour": return `${action} Tour`;
      case "package": return `${action} Package`;
      case "activity": return `${action} Activity`;
      case "transport": return `${action} Transport`;
      case "food": return `${action} Food`;
      default: return `${action} Item`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader className="flex flex-row items-center space-x-3">
          <div className={`w-10 h-10 ${getComponentColor(componentType)} rounded-full flex items-center justify-center`}>
            {getComponentIcon(componentType)}
          </div>
          <DialogTitle>{getModalTitle()}</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Flight Component - Special UI */}
            {componentType === "flight" ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium">Flight Information</h3>
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="manualEntry" className="text-sm">Manual Entry</Label>
                    <Switch 
                      id="manualEntry" 
                      checked={isManualEntry} 
                      onCheckedChange={(checked) => {
                        setIsManualEntry(checked);
                        if (!checked) {
                          // Reset search tab when switching back to search
                          setFlightSearchTab('search');
                        }
                      }} 
                    />
                  </div>
                </div>
                
                {isManualEntry ? (
                  /* Manual Flight Entry Form */
                  <>
                    <FormField
                      control={form.control}
                      name="details.tripType"
                      defaultValue="roundTrip"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel>Flight Type</FormLabel>
                          <FormControl>
                            <RadioGroup 
                              defaultValue={field.value || "roundTrip"}
                              className="flex space-x-4"
                              onValueChange={field.onChange}
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="roundTrip" id="roundTrip" />
                                <Label htmlFor="roundTrip">Round Trip</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="oneWay" id="oneWay" />
                                <Label htmlFor="oneWay">One Way</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="multiCity" id="multiCity" />
                                <Label htmlFor="multiCity">Multi-City</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Flight Title</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* Different UI based on flight type */}
                    {form.watch("details.tripType") === "oneWay" && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="details.legs.0.departureAirport"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>From</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Departure Airport" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="details.legs.0.arrivalAirport"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>To</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Arrival Airport" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        
                        <FormField
                          control={form.control}
                          name="startDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Departure Date</FormLabel>
                              <FormControl>
                                <Input 
                                  type="date" 
                                  name={field.name}
                                  ref={field.ref}
                                  onBlur={field.onBlur}
                                  value={field.value ? (typeof field.value === 'string' ? field.value : format(new Date(field.value), "yyyy-MM-dd")) : ""}
                                  disabled={field.disabled}
                                  min={format(new Date(), "yyyy-MM-dd")} 
                                  onChange={e => {
                                    field.onChange(e.target.value);
                                    
                                    // Calculate and update day index
                                    const newDayIndex = calculateDayIndex(e.target.value);
                                    form.setValue("dayIndex", newDayIndex);
                                    
                                    // Update flight details
                                    const details = form.getValues("details");
                                    if (details?.legs?.[0]) {
                                      details.legs[0].departureDate = e.target.value;
                                      form.setValue("details", details);
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                    
                    {form.watch("details.tripType") === "roundTrip" && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="details.legs.0.departureAirport"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>From</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Departure Airport" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="details.legs.0.arrivalAirport"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>To</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Arrival Airport" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="startDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Departure Date</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="date" 
                                    name={field.name}
                                    ref={field.ref}
                                    onBlur={field.onBlur}
                                    value={field.value ? (typeof field.value === 'string' ? field.value : format(new Date(field.value), "yyyy-MM-dd")) : ""}
                                    disabled={field.disabled}
                                    min={format(new Date(), "yyyy-MM-dd")} 
                                    onChange={e => {
                                      field.onChange(e.target.value);
                                      
                                      // Calculate and update day index
                                      const newDayIndex = calculateDayIndex(e.target.value);
                                      form.setValue("dayIndex", newDayIndex);
                                      
                                      // Update flight details
                                      const details = form.getValues("details");
                                      if (details?.legs?.[0]) {
                                        details.legs[0].departureDate = e.target.value;
                                        form.setValue("details", details);
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="details.legs.0.returnDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Return Date</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="date" 
                                    name={field.name}
                                    ref={field.ref}
                                    onBlur={field.onBlur}
                                    value={field.value || ""}
                                    disabled={field.disabled}
                                    min={form.getValues("startDate") || format(new Date(), "yyyy-MM-dd")} 
                                    onChange={e => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </>
                    )}
                    
                    {form.watch("details.tripType") === "multiCity" && (
                      <div className="space-y-6">
                        {[0, 1].map((legIndex) => (
                          <div key={legIndex} className="border border-border rounded-md p-4 space-y-4">
                            <div className="flex justify-between items-center">
                              <h4 className="text-sm font-medium">Flight Segment {legIndex + 1}</h4>
                              {legIndex > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    // Remove this leg
                                    const details = form.getValues("details") || {};
                                    if (details.legs?.length > 2) {
                                      const updatedLegs = [...details.legs];
                                      updatedLegs.splice(legIndex, 1);
                                      form.setValue("details", { ...details, legs: updatedLegs });
                                    }
                                  }}
                                >
                                  <XIcon className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <FormItem>
                                <FormLabel>From</FormLabel>
                                <div className="relative">
                                  <Input 
                                    placeholder="Departure Airport"
                                    value={form.getValues(`details.legs.${legIndex}.departureAirport`) || ""}
                                    onChange={(e) => {
                                      const details = form.getValues("details") || {};
                                      const updatedLegs = details.legs || [];
                                      
                                      // Ensure we have enough legs
                                      while (updatedLegs.length <= legIndex) {
                                        updatedLegs.push({});
                                      }
                                      
                                      updatedLegs[legIndex] = { 
                                        ...updatedLegs[legIndex], 
                                        departureAirport: e.target.value 
                                      };
                                      
                                      form.setValue("details", { ...details, legs: updatedLegs });
                                    }}
                                  />
                                </div>
                              </FormItem>
                              
                              <FormItem>
                                <FormLabel>To</FormLabel>
                                <div className="relative">
                                  <Input 
                                    placeholder="Arrival Airport"
                                    value={form.getValues(`details.legs.${legIndex}.arrivalAirport`) || ""}
                                    onChange={(e) => {
                                      const details = form.getValues("details") || {};
                                      const updatedLegs = details.legs || [];
                                      
                                      // Ensure we have enough legs
                                      while (updatedLegs.length <= legIndex) {
                                        updatedLegs.push({});
                                      }
                                      
                                      updatedLegs[legIndex] = { 
                                        ...updatedLegs[legIndex], 
                                        arrivalAirport: e.target.value 
                                      };
                                      
                                      form.setValue("details", { ...details, legs: updatedLegs });
                                    }}
                                  />
                                </div>
                              </FormItem>
                            </div>
                            
                            <FormItem>
                              <FormLabel>Date</FormLabel>
                              <div className="relative">
                                <Input 
                                  type="date"
                                  min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                                  max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                                  value={form.getValues(`details.legs.${legIndex}.departureDate`) || 
                                        (legIndex === 0 ? defaultStartDate : "")}
                                  onChange={(e) => {
                                    const details = form.getValues("details") || {};
                                    const updatedLegs = details.legs || [];
                                    
                                    // Ensure we have enough legs
                                    while (updatedLegs.length <= legIndex) {
                                      updatedLegs.push({});
                                    }
                                    
                                    updatedLegs[legIndex] = { 
                                      ...updatedLegs[legIndex], 
                                      departureDate: e.target.value 
                                    };
                                    
                                    form.setValue("details", { ...details, legs: updatedLegs });
                                    
                                    // Set trip start date based on first leg
                                    if (legIndex === 0) {
                                      form.setValue("startDate", e.target.value);
                                      const newDayIndex = calculateDayIndex(e.target.value);
                                      form.setValue("dayIndex", newDayIndex);
                                    }
                                  }}
                                />
                              </div>
                            </FormItem>
                          </div>
                        ))}
                        
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            const details = form.getValues("details") || {};
                            const updatedLegs = details.legs || [];
                            
                            // Add a new empty leg
                            updatedLegs.push({});
                            form.setValue("details", { ...details, legs: updatedLegs });
                          }}
                        >
                          <PlusIcon className="h-4 w-4 mr-2" />
                          Add Flight Segment
                        </Button>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Departure Time</FormLabel>
                            <FormControl>
                              <Input 
                                type="time" 
                                {...field} 
                                value={field.value || ""} 
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  
                                  // Update flight details
                                  const details = form.getValues("details");
                                  if (details?.legs?.[0]?.segments?.[0]) {
                                    details.legs[0].segments[0].departureTime = e.target.value;
                                    form.setValue("details", details);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="endTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Arrival Time</FormLabel>
                            <FormControl>
                              <Input 
                                type="time" 
                                {...field} 
                                value={field.value || ""} 
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  
                                  // Update flight details
                                  const details = form.getValues("details");
                                  if (details?.legs?.[0]?.segments?.[0]) {
                                    details.legs[0].segments[0].arrivalTime = e.target.value;
                                    form.setValue("details", details);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="details.legs.0.segments.0.airline"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Airline</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. American Airlines" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="details.legs.0.segments.0.flightNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Flight Number</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. AA123" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="details.cabinClass"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cabin Class</FormLabel>
                          <Select 
                            defaultValue={field.value}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select cabin class" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="economy">Economy</SelectItem>
                              <SelectItem value="premiumEconomy">Premium Economy</SelectItem>
                              <SelectItem value="business">Business</SelectItem>
                              <SelectItem value="firstClass">First Class</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea {...field} value={field.value || ""} placeholder="Additional information about this flight" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : (
                  /* Flight Search Interface */
                  <Tabs 
                    value={flightSearchTab} 
                    onValueChange={(value) => {
                      if (value === 'search' || value === 'results') {
                        setFlightSearchTab(value);
                      }
                    }}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="search">Search</TabsTrigger>
                      <TabsTrigger value="results" disabled={flightResults.length === 0}>Results</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="search" className="space-y-4 mt-2">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="details.tripType"
                          defaultValue="roundTrip"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel>Flight Type</FormLabel>
                              <FormControl>
                                <RadioGroup 
                                  defaultValue={field.value || "roundTrip"}
                                  className="flex space-x-4"
                                  onValueChange={field.onChange}
                                >
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="roundTrip" id="roundTrip" />
                                    <Label htmlFor="roundTrip">Round Trip</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="oneWay" id="oneWay" />
                                    <Label htmlFor="oneWay">One Way</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="multiCity" id="multiCity" />
                                    <Label htmlFor="multiCity">Multi-City</Label>
                                  </div>
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        {/* Flight legs based on flight type */}
                        {(form.watch("details.tripType") === "roundTrip" || form.watch("details.tripType") === "oneWay") && (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <FormItem>
                                <FormLabel htmlFor="flightFrom">From</FormLabel>
                                <SimpleAirportInput
                                  id="departure-airport"
                                  placeholder="Departure airport or city"
                                  defaultValue={form.getValues("details.legs.0.departureAirport") || ""}
                                  onSelect={(airport) => {
                                    const details = form.getValues("details") || {};
                                    const updatedLegs = details.legs || [{}];
                                    
                                    updatedLegs[0] = { 
                                      ...updatedLegs[0], 
                                      departureAirport: airport.fullName,
                                      departureAirportCode: airport.code
                                    };
                                    
                                    form.setValue("details", { ...details, legs: updatedLegs });
                                  }}
                                  className="pr-8"
                                />
                              </FormItem>
                              
                              <FormItem>
                                <FormLabel htmlFor="flightTo">To</FormLabel>
                                <SimpleAirportInput
                                  id="arrival-airport"
                                  placeholder="Arrival airport or city"
                                  defaultValue={form.getValues("details.legs.0.arrivalAirport") || ""}
                                  onSelect={(airport) => {
                                    const details = form.getValues("details") || {};
                                    const updatedLegs = details.legs || [{}];
                                    
                                    updatedLegs[0] = { 
                                      ...updatedLegs[0], 
                                      arrivalAirport: airport.fullName,
                                      arrivalAirportCode: airport.code
                                    };
                                    
                                    form.setValue("details", { ...details, legs: updatedLegs });
                                  }}
                                  className="pr-8"
                                />
                              </FormItem>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="details.legs.0.departureDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel htmlFor="departureDate">Departure Date</FormLabel>
                                    <FormControl>
                                      <Input 
                                        id="departureDate" 
                                        type="date"
                                        min={format(new Date(), "yyyy-MM-dd")}
                                        {...field}
                                        value={field.value || defaultStartDate}
                                        onChange={(e) => {
                                          field.onChange(e.target.value);
                                          
                                          // Update form start date
                                          form.setValue("startDate", e.target.value);
                                          
                                          // Calculate day index
                                          const newDayIndex = calculateDayIndex(e.target.value);
                                          form.setValue("dayIndex", newDayIndex);
                                        }}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              {form.watch("details.tripType") === "roundTrip" && (
                                <FormField
                                  control={form.control}
                                  name="details.legs.0.returnDate"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel htmlFor="returnDate">Return Date</FormLabel>
                                      <FormControl>
                                        <Input 
                                          id="returnDate" 
                                          type="date"
                                          min={form.getValues("details.legs.0.departureDate") || format(new Date(), "yyyy-MM-dd")}
                                          {...field}
                                          value={field.value || ""}
                                          onChange={(e) => {
                                            field.onChange(e.target.value);
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                          </>
                        )}
                        
                        {form.watch("details.tripType") === "multiCity" && (
                          <div className="space-y-6">
                            {[0, 1].map((legIndex) => (
                              <div key={legIndex} className="border border-border rounded-md p-4 space-y-4">
                                <div className="flex justify-between items-center">
                                  <h4 className="text-sm font-medium">Flight Segment {legIndex + 1}</h4>
                                  {legIndex > 1 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        // Remove this leg
                                        const details = form.getValues("details") || {};
                                        if (details.legs?.length > 2) {
                                          const updatedLegs = [...details.legs];
                                          updatedLegs.splice(legIndex, 1);
                                          form.setValue("details", { ...details, legs: updatedLegs });
                                        }
                                      }}
                                    >
                                      <XIcon className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <FormItem>
                                    <FormLabel>From</FormLabel>
                                    <SimpleAirportInput
                                      id={`departure-airport-${legIndex}`}
                                      placeholder="Departure airport or city"
                                      defaultValue={form.getValues(`details.legs.${legIndex}.departureAirport`) || ""}
                                      onSelect={(airport) => {
                                        const details = form.getValues("details") || {};
                                        const updatedLegs = details.legs || [];
                                        
                                        // Ensure we have enough legs
                                        while (updatedLegs.length <= legIndex) {
                                          updatedLegs.push({});
                                        }
                                        
                                        updatedLegs[legIndex] = { 
                                          ...updatedLegs[legIndex], 
                                          departureAirport: airport.fullName,
                                          departureAirportCode: airport.code
                                        };
                                        
                                        form.setValue("details", { ...details, legs: updatedLegs });
                                      }}
                                      className="pr-8"
                                    />
                                  </FormItem>
                                  
                                  <FormItem>
                                    <FormLabel>To</FormLabel>
                                    <SimpleAirportInput
                                      id={`arrival-airport-${legIndex}`}
                                      placeholder="Arrival airport or city"
                                      defaultValue={form.getValues(`details.legs.${legIndex}.arrivalAirport`) || ""}
                                      onSelect={(airport) => {
                                        const details = form.getValues("details") || {};
                                        const updatedLegs = details.legs || [];
                                        
                                        // Ensure we have enough legs
                                        while (updatedLegs.length <= legIndex) {
                                          updatedLegs.push({});
                                        }
                                        
                                        updatedLegs[legIndex] = { 
                                          ...updatedLegs[legIndex], 
                                          arrivalAirport: airport.fullName,
                                          arrivalAirportCode: airport.code
                                        };
                                        
                                        form.setValue("details", { ...details, legs: updatedLegs });
                                      }}
                                      className="pr-8"
                                    />
                                  </FormItem>
                                </div>
                                
                                <FormItem>
                                  <FormLabel>Date</FormLabel>
                                  <Input 
                                    type="date"
                                    min={format(new Date(), "yyyy-MM-dd")}
                                    value={form.getValues(`details.legs.${legIndex}.departureDate`) || 
                                          (legIndex === 0 ? defaultStartDate : "")}
                                    onChange={(e) => {
                                      const details = form.getValues("details") || {};
                                      const updatedLegs = details.legs || [];
                                      
                                      // Ensure we have enough legs
                                      while (updatedLegs.length <= legIndex) {
                                        updatedLegs.push({});
                                      }
                                      
                                      updatedLegs[legIndex] = { 
                                        ...updatedLegs[legIndex], 
                                        departureDate: e.target.value 
                                      };
                                      
                                      form.setValue("details", { ...details, legs: updatedLegs });
                                      
                                      // Set trip start date based on first leg
                                      if (legIndex === 0) {
                                        form.setValue("startDate", e.target.value);
                                        const newDayIndex = calculateDayIndex(e.target.value);
                                        form.setValue("dayIndex", newDayIndex);
                                      }
                                    }}
                                  />
                                </FormItem>
                              </div>
                            ))}
                            
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              onClick={() => {
                                const details = form.getValues("details") || {};
                                const updatedLegs = details.legs || [];
                                
                                // Add a new empty leg
                                updatedLegs.push({});
                                form.setValue("details", { ...details, legs: updatedLegs });
                              }}
                            >
                              <PlusIcon className="h-4 w-4 mr-2" />
                              Add Flight Segment
                            </Button>
                          </div>
                        )}
                        
                        <FormField
                          control={form.control}
                          name="details.cabinClass"
                          defaultValue="economy"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel htmlFor="cabinClass">Cabin Class</FormLabel>
                              <Select 
                                onValueChange={field.onChange}
                                defaultValue={field.value || "economy"}
                              >
                                <FormControl>
                                  <SelectTrigger id="cabinClass">
                                    <SelectValue placeholder="Select cabin class" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="economy">Economy</SelectItem>
                                  <SelectItem value="premiumEconomy">Premium Economy</SelectItem>
                                  <SelectItem value="business">Business</SelectItem>
                                  <SelectItem value="firstClass">First Class</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <div className="flex justify-between items-center">
                          <FormField
                            control={form.control}
                            name="details.passengers"
                            defaultValue={1}
                            render={({ field }) => (
                              <FormItem className="flex items-center space-x-2">
                                <FormLabel htmlFor="passengers">Passengers</FormLabel>
                                <Select 
                                  onValueChange={(value) => field.onChange(parseInt(value))}
                                  defaultValue={String(field.value || 1)}
                                >
                                  <FormControl>
                                    <SelectTrigger id="passengers" className="w-20">
                                      <SelectValue placeholder="1" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                      <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                          
                          <Button 
                            type="button" 
                            onClick={() => {
                              const details = form.getValues("details") || {};
                              searchFlights({
                                from: details?.legs?.[0]?.departureAirport,
                                to: details?.legs?.[0]?.arrivalAirport,
                                departureDate: details?.legs?.[0]?.departureDate || defaultStartDate || format(new Date(), "yyyy-MM-dd"),
                                cabinClass: details?.cabinClass || "economy",
                                passengers: details?.passengers || 1,
                                tripType: details?.tripType || "roundTrip"
                              });
                            }}
                            className="gap-2"
                            disabled={isSearching}
                          >
                            {isSearching ? (
                              <>
                                <LoaderIcon className="h-4 w-4 animate-spin" />
                                Searching...
                              </>
                            ) : (
                              <>
                                <SearchIcon className="h-4 w-4" />
                                Search Flights
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="results" className="space-y-4 mt-2">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-base font-semibold">
                            {flightResults.length > 0 ? `${flightResults.length} flights found` : "No flights found"}
                          </h3>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setFlightSearchTab('search')}
                          >
                            Modify Search
                          </Button>
                        </div>
                        
                        {flightResults.map((flight) => (
                          <Card 
                            key={flight.id} 
                            className={`cursor-pointer transition-colors ${selectedFlight?.id === flight.id ? 'border-primary ring-1 ring-primary' : ''}`}
                            onClick={() => handleSelectFlight(flight)}
                          >
                            <CardContent className="p-4">
                              <div className="flex justify-between items-center mb-2">
                                <div className="font-medium">{flight.airline}</div>
                                <div className="text-lg font-semibold">${flight.price}</div>
                              </div>
                              
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-sm">
                                  <div className="font-semibold">{flight.departureTime}</div>
                                  <div className="text-muted-foreground">{flight.departureAirportCode}</div>
                                </div>
                                
                                <div className="flex-1 px-4 flex flex-col items-center">
                                  <div className="text-xs text-muted-foreground">{flight.duration}</div>
                                  <div className="w-full flex items-center">
                                    <div className="h-[2px] flex-1 bg-muted" />
                                    <PlaneIcon className="h-3 w-3 mx-1 text-muted-foreground" />
                                    <div className="h-[2px] flex-1 bg-muted" />
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {flight.stops === 0 ? "Nonstop" : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                                  </div>
                                </div>
                                
                                <div className="text-sm text-right">
                                  <div className="font-semibold">{flight.arrivalTime}</div>
                                  <div className="text-muted-foreground">{flight.arrivalAirportCode}</div>
                                </div>
                              </div>
                              
                              <div className="flex justify-between items-center text-xs text-muted-foreground">
                                <div>Flight {flight.flightNumber}</div>
                                <div className="capitalize">{flight.cabinClass}</div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                )}
              </>
            ) : componentType === "hotel" ? (
              // Hotel Component Form
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hotel Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter hotel name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="details.hotelChain"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hotel Chain</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Marriott, Hilton" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.rating"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Star Rating</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select rating" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">1 Star</SelectItem>
                            <SelectItem value="2">2 Stars</SelectItem>
                            <SelectItem value="3">3 Stars</SelectItem>
                            <SelectItem value="4">4 Stars</SelectItem>
                            <SelectItem value="5">5 Stars</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Hotel address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Check-in Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Check-out Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.checkInTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Check-in Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.checkOutTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Check-out Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="details.numberOfRooms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rooms</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="1" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map(num => (
                              <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.numberOfGuests"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Guests</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="1" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                              <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.totalPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Price</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="0.00"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.roomType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Room Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select room type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="standard">Standard Room</SelectItem>
                            <SelectItem value="deluxe">Deluxe Room</SelectItem>
                            <SelectItem value="suite">Suite</SelectItem>
                            <SelectItem value="junior-suite">Junior Suite</SelectItem>
                            <SelectItem value="presidential">Presidential Suite</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.bedType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bed Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select bed type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="single">Single Bed</SelectItem>
                            <SelectItem value="double">Double Bed</SelectItem>
                            <SelectItem value="queen">Queen Bed</SelectItem>
                            <SelectItem value="king">King Bed</SelectItem>
                            <SelectItem value="twin">Twin Beds</SelectItem>
                            <SelectItem value="sofa">Sofa Bed</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.confirmationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmation Number</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Hotel confirmation number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="details.specialRequests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Special Requests</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Any special requests or notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : componentType === "cruise" ? (
              // Cruise Component Form
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cruise Title</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter cruise name or itinerary" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.cruiseLine"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cruise Line</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Royal Caribbean, Norwegian" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.shipName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ship Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Name of the cruise ship" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.embarkationPort"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Embarkation Port</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Departure port" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.disembarkationPort"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Disembarkation Port</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Arrival port" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Embarkation Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Disembarkation Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="details.cabinType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cabin Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select cabin type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="interior">Interior</SelectItem>
                            <SelectItem value="oceanview">Ocean View</SelectItem>
                            <SelectItem value="balcony">Balcony</SelectItem>
                            <SelectItem value="suite">Suite</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.cabinNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cabin Number</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. 7234" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.deckLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deck Level</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Deck 7" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.numberOfGuests"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of Guests</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="1" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6].map(num => (
                              <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.totalPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Price</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="0.00"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.confirmationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmation Number</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Cruise confirmation number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="details.diningPackage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dining Package</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Specialty Dining" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.beveragePackage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Beverage Package</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Unlimited Beverage" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.wifiPackage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>WiFi Package</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Premium WiFi" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.specialRequests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Special Requests</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Any special requests or notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : componentType === "travel-insurance" ? (
              // Travel Insurance Component Form
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Insurance Policy Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter insurance policy name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.provider"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Insurance Provider</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Allianz, World Nomads" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.policyNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Policy Number</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Insurance policy number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.policyType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Policy Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select policy type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="comprehensive">Comprehensive</SelectItem>
                            <SelectItem value="medical">Medical Only</SelectItem>
                            <SelectItem value="trip-cancellation">Trip Cancellation</SelectItem>
                            <SelectItem value="baggage">Baggage Protection</SelectItem>
                            <SelectItem value="adventure">Adventure Sports</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.premium"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Premium Amount</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="0.00"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Effective Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Expiration Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.coverageAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Coverage Amount</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field} 
                          placeholder="0.00"
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.emergencyContactNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Emergency Contact</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="24/7 emergency number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.claimsContactNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Claims Contact</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Claims department number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.specialNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Special Notes</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Important policy details or notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : componentType === "package" ? (
              // Package Component Form (Land + Air Packages)
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Package Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter package name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.supplier"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Supplier</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Apple Leisure Group, Funjet, Pleasant Holidays" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.packageType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Package Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select package type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="all-inclusive">All-Inclusive Resort</SelectItem>
                            <SelectItem value="traditional-resort">Traditional Resort</SelectItem>
                            <SelectItem value="hotel-package">Hotel Package</SelectItem>
                            <SelectItem value="city-package">City Package</SelectItem>
                            <SelectItem value="beach-package">Beach Package</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Package always includes air, so we use tabs */}
                <Tabs defaultValue="accommodation" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="accommodation">Accommodation</TabsTrigger>
                    <TabsTrigger value="air">Air Details</TabsTrigger>
                    <TabsTrigger value="package">Package Info</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="accommodation" className="space-y-4 mt-4">
                    {/* Accommodation Details */}
                    <FormField
                      control={form.control}
                      name="details.resortName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Resort/Hotel Name</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Name of resort or hotel" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Destination</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Resort location/destination" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Check-in Date</FormLabel>
                            <FormControl>
                              <Input 
                                type="date" 
                                {...field}
                                min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                                max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                                onChange={e => {
                                  field.onChange(e.target.value);
                                  const newDayIndex = calculateDayIndex(e.target.value);
                                  form.setValue("dayIndex", newDayIndex);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="endDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Check-out Date</FormLabel>
                            <FormControl>
                              <Input 
                                type="date" 
                                {...field}
                                min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                                max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="details.roomType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Room Type</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. Ocean View, Junior Suite" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.boardBasis"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Board Basis</FormLabel>
                            <Select onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select board basis" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="all-inclusive">All-Inclusive</SelectItem>
                                <SelectItem value="full-board">Full Board</SelectItem>
                                <SelectItem value="half-board">Half Board</SelectItem>
                                <SelectItem value="breakfast-only">Breakfast Only</SelectItem>
                                <SelectItem value="room-only">Room Only</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="details.numberOfGuests"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Number of Guests</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              {...field} 
                              placeholder="Number of guests"
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>
                  
                  <TabsContent value="air" className="space-y-4 mt-4">
                    {/* Air Details Content */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700">Flight Information</h4>
                      
                      <FormField
                        control={form.control}
                        name="details.flightDetails.tripType"
                        defaultValue="roundTrip"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel>Flight Type</FormLabel>
                            <FormControl>
                              <RadioGroup 
                                defaultValue={field.value || "roundTrip"}
                                className="flex space-x-4"
                                onValueChange={field.onChange}
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="roundTrip" id="roundTrip" />
                                  <Label htmlFor="roundTrip">Round Trip</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="oneWay" id="oneWay" />
                                  <Label htmlFor="oneWay">One Way</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="details.flightDetails.legs.0.departureAirport"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Departure Airport</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Departure airport" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="details.flightDetails.legs.0.arrivalAirport"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Arrival Airport</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Arrival airport" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="details.flightDetails.cabinClass"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Cabin Class</FormLabel>
                              <Select onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select cabin class" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="economy">Economy</SelectItem>
                                  <SelectItem value="premiumEconomy">Premium Economy</SelectItem>
                                  <SelectItem value="business">Business</SelectItem>
                                  <SelectItem value="firstClass">First Class</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="details.flightDetails.passengers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Passengers</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  {...field} 
                                  placeholder="Number of passengers"
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="package" className="space-y-4 mt-4">
                    {/* Package Information */}
                    <FormField
                      control={form.control}
                      name="details.confirmationNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirmation Number</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Package confirmation number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="details.totalPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total Package Price</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              {...field} 
                              placeholder="0.00"
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="details.specialRequests"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Special Requests</FormLabel>
                          <FormControl>
                            <Textarea {...field} placeholder="Any special requests or notes" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            ) : componentType === "activity" ? (
              // Activity Component Form
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activity Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter activity name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.activityType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Activity Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select activity type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="sightseeing">Sightseeing</SelectItem>
                            <SelectItem value="adventure">Adventure</SelectItem>
                            <SelectItem value="cultural">Cultural</SelectItem>
                            <SelectItem value="outdoor">Outdoor</SelectItem>
                            <SelectItem value="entertainment">Entertainment</SelectItem>
                            <SelectItem value="sports">Sports</SelectItem>
                            <SelectItem value="wellness">Wellness</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.provider"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Provider</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Activity provider" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Activity location" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="details.numberOfParticipants"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Participants</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="Number of people"
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.difficultyLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Difficulty</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select difficulty" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="easy">Easy</SelectItem>
                            <SelectItem value="moderate">Moderate</SelectItem>
                            <SelectItem value="challenging">Challenging</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.totalPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="0.00"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.confirmationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmation Number</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Activity confirmation number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Activity description and notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : componentType === "transport" ? (
              // Transport Component Form
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Transport Title</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter transport description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.transportType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Transport Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select transport type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="taxi">Taxi</SelectItem>
                            <SelectItem value="uber">Uber/Lyft</SelectItem>
                            <SelectItem value="rental-car">Rental Car</SelectItem>
                            <SelectItem value="bus">Bus</SelectItem>
                            <SelectItem value="train">Train</SelectItem>
                            <SelectItem value="shuttle">Shuttle</SelectItem>
                            <SelectItem value="private-transfer">Private Transfer</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.provider"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Provider</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Transport provider" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.pickupLocation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pickup Location</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Pickup address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.dropoffLocation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Drop-off Location</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Drop-off address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.pickupTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pickup Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.numberOfPassengers"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Passengers</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="Number of passengers"
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.driverName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Driver Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Driver name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.driverContact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Driver Contact</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Driver phone number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.confirmationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmation Number</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Transport confirmation number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.totalPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Price</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="0.00"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.specialRequests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Special Requests</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Any special requests or notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : componentType === "food" ? (
              // Food Component Form
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Restaurant/Meal Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter restaurant or meal name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.restaurantName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Restaurant Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Restaurant name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.cuisineType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cuisine Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select cuisine type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="italian">Italian</SelectItem>
                            <SelectItem value="french">French</SelectItem>
                            <SelectItem value="asian">Asian</SelectItem>
                            <SelectItem value="mexican">Mexican</SelectItem>
                            <SelectItem value="american">American</SelectItem>
                            <SelectItem value="mediterranean">Mediterranean</SelectItem>
                            <SelectItem value="local">Local Cuisine</SelectItem>
                            <SelectItem value="international">International</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Restaurant Address</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Restaurant address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.reservationTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Reservation Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.mealType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meal Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select meal type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="breakfast">Breakfast</SelectItem>
                            <SelectItem value="lunch">Lunch</SelectItem>
                            <SelectItem value="dinner">Dinner</SelectItem>
                            <SelectItem value="brunch">Brunch</SelectItem>
                            <SelectItem value="snack">Snack</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="details.numberOfGuests"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of Guests</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            placeholder="Number of guests"
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.priceRange"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price Range</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select price range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="budget">$ Budget</SelectItem>
                            <SelectItem value="moderate">$$ Moderate</SelectItem>
                            <SelectItem value="upscale">$$$ Upscale</SelectItem>
                            <SelectItem value="fine-dining">$$$$ Fine Dining</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.dressCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dress Code</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select dress code" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="casual">Casual</SelectItem>
                            <SelectItem value="smart-casual">Smart Casual</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="formal">Formal</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.confirmationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmation Number</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Reservation confirmation number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.contactNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Restaurant Phone</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Restaurant phone number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details.specialRequests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Special Requests</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Dietary restrictions, special occasions, etc." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : componentType === "tour" ? (
              // Tour Component Form (Escorted Tours)
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tour Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter escorted tour name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="details.tourOperator"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tour Operator</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Globus, Abercrombie & Kent, Collette, CIE Tours" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="details.tourType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tour Type</FormLabel>
                        <Select onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select tour type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="escorted-group">Escorted Group Tour</SelectItem>
                            <SelectItem value="private-group">Private Group Tour</SelectItem>
                            <SelectItem value="small-group">Small Group Tour</SelectItem>
                            <SelectItem value="luxury-escorted">Luxury Escorted Tour</SelectItem>
                            <SelectItem value="river-cruise-tour">River Cruise Tour</SelectItem>
                            <SelectItem value="adventure-tour">Adventure Tour</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Includes Air Checkbox */}
                <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                  <FormField
                    control={form.control}
                    name="details.includesAir"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value || false}
                            onChange={field.onChange}
                            className="h-4 w-4"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium">
                            Includes Air Transportation
                          </FormLabel>
                          <p className="text-xs text-muted-foreground">
                            Check if this tour package includes flights
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Conditional Tabs based on Includes Air */}
                {form.watch("details.includesAir") ? (
                  <Tabs defaultValue="tour" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="tour">Tour Details</TabsTrigger>
                      <TabsTrigger value="air">Air Details</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="tour" className="space-y-4 mt-4">
                      {/* Tour Details Content */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="startDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tour Start Date</FormLabel>
                              <FormControl>
                                <Input 
                                  type="date" 
                                  {...field}
                                  min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                                  max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                                  onChange={e => {
                                    field.onChange(e.target.value);
                                    const newDayIndex = calculateDayIndex(e.target.value);
                                    form.setValue("dayIndex", newDayIndex);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="details.duration"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Duration</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g. 10 days, 2 weeks" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="details.groupSize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Group Size</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  {...field} 
                                  placeholder="Number of travelers"
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="details.totalPrice"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Total Price</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  {...field} 
                                  placeholder="0.00"
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="details.tourGuide"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tour Director/Guide</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Guide name" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="details.guideLanguage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Guide Language</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g. English, Spanish" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="details.confirmationNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Confirmation Number</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Tour confirmation number" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.specialRequirements"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Special Requirements</FormLabel>
                            <FormControl>
                              <Textarea {...field} placeholder="Any special requirements or notes" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </TabsContent>
                    
                    <TabsContent value="air" className="space-y-4 mt-4">
                      {/* Air Details Content - Reuse flight form components */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">Flight Information</h4>
                        
                        <FormField
                          control={form.control}
                          name="details.flightDetails.tripType"
                          defaultValue="roundTrip"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel>Flight Type</FormLabel>
                              <FormControl>
                                <RadioGroup 
                                  defaultValue={field.value || "roundTrip"}
                                  className="flex space-x-4"
                                  onValueChange={field.onChange}
                                >
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="roundTrip" id="roundTrip" />
                                    <Label htmlFor="roundTrip">Round Trip</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="oneWay" id="oneWay" />
                                    <Label htmlFor="oneWay">One Way</Label>
                                  </div>
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="details.flightDetails.legs.0.departureAirport"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Departure Airport</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Departure airport" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="details.flightDetails.legs.0.arrivalAirport"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Arrival Airport</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Arrival airport" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="details.flightDetails.cabinClass"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Cabin Class</FormLabel>
                                <Select onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select cabin class" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="economy">Economy</SelectItem>
                                    <SelectItem value="premiumEconomy">Premium Economy</SelectItem>
                                    <SelectItem value="business">Business</SelectItem>
                                    <SelectItem value="firstClass">First Class</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="details.flightDetails.passengers"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Passengers</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="number" 
                                    {...field} 
                                    placeholder="Number of passengers"
                                    onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="package" className="space-y-4 mt-4">
                      {/* Package Information */}
                      <FormField
                        control={form.control}
                        name="details.confirmationNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Confirmation Number</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Package confirmation number" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.totalPrice"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Total Package Price</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                {...field} 
                                placeholder="0.00"
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.specialRequests"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Special Requests</FormLabel>
                            <FormControl>
                              <Textarea {...field} placeholder="Any special requests or notes" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  /* Tour Details without Air */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tour Start Date</FormLabel>
                            <FormControl>
                              <Input 
                                type="date" 
                                {...field}
                                min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                                max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                                onChange={e => {
                                  field.onChange(e.target.value);
                                  const newDayIndex = calculateDayIndex(e.target.value);
                                  form.setValue("dayIndex", newDayIndex);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.duration"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Duration</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. 10 days, 2 weeks" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="details.groupSize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Group Size</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                {...field} 
                                placeholder="Number of travelers"
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.totalPrice"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Total Price</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                {...field} 
                                placeholder="0.00"
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="details.tourGuide"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tour Director/Guide</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Guide name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="details.guideLanguage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Guide Language</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. English, Spanish" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="details.confirmationNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirmation Number</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Tour confirmation number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="details.specialRequirements"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Special Requirements</FormLabel>
                          <FormControl>
                            <Textarea {...field} placeholder="Any special requirements or notes" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            ) : (
              // Other component types can go here
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} value={field.value || ""} placeholder="Enter component description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value || ""} placeholder="Location, if applicable" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            name={field.name}
                            ref={field.ref}
                            onBlur={field.onBlur}
                            value={field.value ? (typeof field.value === 'string' ? field.value : format(new Date(field.value), "yyyy-MM-dd")) : ""}
                            disabled={field.disabled}
                            min={dateRange ? format(dateRange[0], "yyyy-MM-dd") : undefined} 
                            max={dateRange ? format(dateRange[1], "yyyy-MM-dd") : undefined}
                            onChange={e => {
                              field.onChange(e.target.value);
                              
                              // Calculate and update day index
                              const newDayIndex = calculateDayIndex(e.target.value);
                              form.setValue("dayIndex", newDayIndex);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <LoaderIcon className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <SaveIcon className="h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
